import type { PlaycastMessage, PlaycastMessageTarget } from '@playcastdotio/Messaging';
import type { MacroPackageCaptureRelay } from '../local-host-session/MacroPackageCaptureRelay';
import type { NativeMacroPackageBridge } from '../../services/playjector/NativeMacroPackageBridge';
import { MACRO_PACKAGE_IPC, type RawPackageUploadSocketBridge } from '../../services/playjector/MacroPackageSocketLoader';

type Message = PlaycastMessage<PlaycastMessageTarget>;
export interface MacroPackageIpcRouterDeps {
  capture: Pick<MacroPackageCaptureRelay, 'receive'>;
  bridge: Pick<NativeMacroPackageBridge, 'receive' | 'handleDisconnect'>;
  uploads: Pick<RawPackageUploadSocketBridge, 'receive'>;
}

/** host-electron tells the renderer that the Playjector TCP connection dropped. */
export const PLAYJECTOR_DISCONNECT_ACTION = 'notifyPlayjectorDisconnect';

/**
 * Delivers main-process IPC to the macro-package consumers: `record/packageInputs` replies to the capture relay,
 * `process/macroPackageRun` replies to the native bridge, raw-upload traffic to the upload bridge, and a
 * Playjector disconnect to `bridge.handleDisconnect()` (failing every running session).
 */
export class MacroPackageIpcRouter {
  public constructor(private readonly deps: MacroPackageIpcRouterDeps) {}

  public receive(message: Message): void {
    if (this.deps.capture.receive(message) || this.deps.bridge.receive(message) || this.deps.uploads.receive(message)) return;
    const { target, action, isReply } = message.header;
    if (target === 'host' && (action as string) === PLAYJECTOR_DISCONNECT_ACTION && !isReply) this.deps.bridge.handleDisconnect();
  }

  /** True for traffic this router owns, so the local-editor forwarder does not echo it to the browser. */
  public claims(message: Message): boolean {
    const { target, isReply } = message.header;
    const action = message.header.action as string;
    if (target === 'record') return action === 'packageInputs' && isReply;
    if (target === 'process') return action === 'macroPackageRun' && isReply;
    return target === 'host' && (action === MACRO_PACKAGE_IPC.socket || action === MACRO_PACKAGE_IPC.uploadComplete || action === 'macroPackageListComplete');
  }
}
